/**
 * Live counts surfaced as badges on the sidebar nav (QC queue, open exceptions).
 * Fetched once from the dashboard tiles and cached so persistent chrome doesn't
 * re-request on every navigation. Degrades gracefully to zero on failure.
 */
import { create } from 'zustand';
import { fetchDashboard } from '@/api/dashboard';
import { lastNDaysRange } from '@/lib/dateRange';

interface NavBadgesState {
  qcQueue: number;
  exceptions: number;
  isLoaded: boolean;
  isLoading: boolean;
  /** Fetch the counts once. Idempotent — safe to call on every Sidebar mount. */
  loadBadges: () => Promise<void>;
  reset: () => void;
}

export const useNavBadgesStore = create<NavBadgesState>((set, get) => ({
  qcQueue: 0,
  exceptions: 0,
  isLoaded: false,
  isLoading: false,

  loadBadges: async () => {
    if (get().isLoading || get().isLoaded) {
      return;
    }
    set({ isLoading: true });
    try {
      const data = await fetchDashboard(lastNDaysRange(30));
      set({
        qcQueue: data?.actionTiles?.qcQueue ?? 0,
        exceptions: data?.actionTiles?.exceptionsToResolve ?? 0,
        isLoaded: true,
      });
    } catch {
      set({ isLoaded: true });
    } finally {
      set({ isLoading: false });
    }
  },

  reset: () => set({ qcQueue: 0, exceptions: 0, isLoaded: false, isLoading: false }),
}));
